import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import ExternalHelpNotice from './ExternalHelpNotice';

// Inspection types a Beställare can request (ClickUp 869fk6pfb). The Swedish
// value is what is stored as serviceType; requests sent with an older type,
// such as Överlåtelsebesiktning, keep their stored text and still display.
export const INSPECTION_TYPES = [
  { value: 'Förbesiktning', key: 'pre' },
  { value: 'Etappbesiktning', key: 'milestone' },
  { value: 'Slutbesiktning', key: 'final' },
  { value: 'Garantibesiktning', key: 'warranty' },
  { value: 'Statusbesiktning', key: 'status' },
  { value: 'Vet inte', key: 'unsure' },
] as const;

const LEGAL_TYPES = [
  { value: 'Avtalsrätt', key: 'serviceTypeContractLaw' },
  { value: 'Entreprenadjuridik', key: 'serviceTypeConstructionLaw' },
  { value: 'Tvistlösning', key: 'serviceTypeDisputeResolution' },
  { value: 'Övrigt', key: 'serviceTypeOther' },
] as const;

export interface ProviderRequestDraft {
  serviceType: string;
  description: string;
  timeframe: string;
}

export const emptyProviderRequestDraft = (): ProviderRequestDraft => ({ serviceType: '', description: '', timeframe: 'normal' });

export const isProviderRequestDraftComplete = (draft: ProviderRequestDraft) =>
  !!draft.serviceType && !!draft.description.trim();

interface ProviderRequestFieldsProps {
  providerType: 'lawyer' | 'inspector';
  value: ProviderRequestDraft;
  onChange: (next: ProviderRequestDraft) => void;
  /** Shown first, e.g. the project choice on Extern hjälp, where the project is not given. */
  before?: React.ReactNode;
}

/**
 * The fields of a request to a Jurist or Besiktningsman, shared by the project's
 * Extern hjälp tab and the Extern hjälp page so both dialogs look the same.
 */
export function ProviderRequestFields({ providerType, value, onChange, before }: ProviderRequestFieldsProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const set = (patch: Partial<ProviderRequestDraft>) => onChange({ ...value, ...patch });
  const inspectionType = providerType === 'inspector'
    ? INSPECTION_TYPES.find(item => item.value === value.serviceType)
    : undefined;

  return (
    <>
      <ExternalHelpNotice />
      {before}
      <div className="mb-3">
        <label htmlFor="provider-request-service-type" className="form-label fw-semibold">{t('marketplace.request.serviceType')}</label>
        <select
          id="provider-request-service-type"
          className="form-select service-type-select"
          aria-describedby={inspectionType ? 'inspection-type-help' : undefined}
          value={value.serviceType}
          onChange={e => set({ serviceType: e.target.value })}
        >
          <option value="">— {t('marketplace.request.serviceType')} —</option>
          {providerType === 'lawyer'
            ? LEGAL_TYPES.map(({ value: option, key }) => (
              <option key={option} value={option}>{t(`marketplace.${key}`)}</option>
            ))
            : INSPECTION_TYPES.map(({ value: option, key }) => (
              <option key={option} value={option}>{t(`marketplace.inspectionTypes.${key}.label`)}</option>
            ))}
        </select>
        {inspectionType && (
          <div id="inspection-type-help" className="form-text" data-testid="inspection-type-help">
            {t(`marketplace.inspectionTypes.${inspectionType.key}.help`)}
          </div>
        )}
      </div>
      <div className="mb-3">
        <label htmlFor="provider-request-description" className="form-label fw-semibold">{t('marketplace.request.description')}</label>
        <textarea
          id="provider-request-description"
          className="form-control"
          rows={4}
          placeholder={t('marketplace.request.descriptionPlaceholder')}
          value={value.description}
          onChange={e => set({ description: e.target.value })}
        />
      </div>
      <div className="mb-3">
        <label htmlFor="provider-request-timeframe" className="form-label fw-semibold">{t('marketplace.request.timeframe')}</label>
        <select id="provider-request-timeframe" className="form-select" value={value.timeframe} onChange={e => set({ timeframe: e.target.value })}>
          <option value="urgent">{t('marketplace.request.timeframes.urgent')}</option>
          <option value="soon">{t('marketplace.request.timeframes.soon')}</option>
          <option value="normal">{t('marketplace.request.timeframes.normal')}</option>
          <option value="flexible">{t('marketplace.request.timeframes.flexible')}</option>
        </select>
      </div>
      <div className="mb-3">
        <label className="form-label fw-semibold">{t('marketplace.request.contactInfo')}</label>
        <div className="row g-2">
          <div className="col-md-6"><input className="form-control" value={user?.fullName || ''} disabled /></div>
          <div className="col-md-6"><input className="form-control" value={user?.email || ''} disabled /></div>
        </div>
        <small className="text-muted">{t('marketplace.request.contactInfoNote')}</small>
      </div>
      <div className="alert alert-success d-flex align-items-center mb-0">
        <i className="feather-shield me-2"></i>
        {t('marketplace.request.securityNote')}
      </div>
    </>
  );
}
